import { MouseEventHandler, useCallback, useState } from 'react';
import Client from 'typings/Client';
import { Table, Tbody, Td, Thead, Tr, Th, Line } from './styles';

type SortOrder = 'ascn' | 'desc';

function sortData({
  tableData,
  sortKey,
  reverse,
}: {
  tableData: Client[];
  sortKey: keyof Client;
  reverse: boolean;
}) {
  if (!sortKey) return tableData;

  const sortedData = tableData.sort((a, b) => {
    return a[sortKey] > b[sortKey] ? 1 : -1;
  });

  if (reverse) {
    return sortedData.reverse();
  }

  return sortedData;
}

function SortButton({
  sortOrder,
  columnKey,
  sortKey,
  onClick,
}: {
  sortOrder: SortOrder;
  columnKey: keyof Client;
  sortKey: keyof Client;
  onClick: MouseEventHandler<HTMLButtonElement>;
}) {
  return (
    // eslint-disable-next-line react/button-has-type
    <button
      onClick={onClick}
      className={`${sortKey === columnKey && sortOrder === 'desc'
          ? 'sort-button sort-reverse'
          : 'sort-button'
        }`}
    >
      ▲
    </button>
  );
}

// eslint-disable-next-line no-shadow
function SortableTable({ data }: { data: Client[] }) {
  const [sortKey, setSortKey] = useState<keyof Client>('companyName');
  const [sortOrder, setSortOrder] = useState<SortOrder>('ascn');

  const headers: { key: keyof Client; label: string }[] = [
    { key: 'clientId', label: 'ID' },
    { key: 'cnpj', label: 'CNPJ' },
    { key: 'companyName', label: 'Company' },
    { key: 'contactName', label: 'Contact' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
  ];
  const sortedData = useCallback(
    () => sortData({ tableData: data, sortKey, reverse: sortOrder === 'desc' }),
    [data, sortKey, sortOrder],
  );

  function changeSort(key: keyof Client) {
    setSortOrder(sortOrder === 'ascn' ? 'desc' : 'ascn');

    setSortKey(key);
  }

  return (
    <Table>
      <Thead>
        <Tr>
          {headers.map(row => {
            return (
              <Th key={row.key}>
                {row.label}{' '}
                <SortButton
                  columnKey={row.key}
                  onClick={() => changeSort(row.key)}
                  {...{
                    sortOrder,
                    sortKey,
                  }}
                />
              </Th>
            );
          })}
        </Tr>
      </Thead>

      <Line />

      <Tbody>
        {sortedData().map(person => {
          return (
            <Tr>
              <Td>{person.clientId}</Td>
              <Td>{person.cnpj}</Td>
              <Td>{person.companyName}</Td>
              <Td>{person.contactName}</Td>
              <Td>{person.email}</Td>
              <Td>{person.phone}</Td>
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );
}

export default SortableTable;
